/**
 * GitHub Sync Client
 *
 * Syncs project tracking data (PROGRESS.md, TODO comments) to GitHub Projects and Issues.
 */

import { readFile, readdir, stat, writeFile } from 'fs/promises';
import { join, dirname } from 'path';
import { existsSync } from 'fs';
import { fileURLToPath } from 'url';
import { runOutput } from '../utils/exec.js';
import type { ProgressData } from './progress-parser.js';
import { fileURLToPath as _fileURLToPath } from 'url';

// CommonJS __dirname equivalent
// In ESM: dirname(fileURLToPath(import.meta.url))
// In CJS: use global __dirname
let __dirname: string;
try {
  // @ts-ignore - import.meta may not exist in CJS
  __dirname = dirname(_fileURLToPath((globalThis as unknown as Record<string, unknown>).import?.meta?.url || ''));
} catch {
  // Fallback to empty string - paths will be resolved relative to cwd
  __dirname = '';
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface GitHubProject {
  id: string;
  owner: string;
  name: string;
  url: string;
  number: number;
}

export interface GitHubIssue {
  number: number;
  title: string;
  state: 'open' | 'closed';
  labels: string[];
  body: string;
  url: string;
}

export interface TodoComment {
  file: string;
  line: number;
  type: 'TODO' | 'FIXME';
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  content: string;
  feature: string;
}

export interface SprintInfo {
  name: string;
  status: 'Not Started' | 'In Progress' | 'Completed';
  items: string[];
}

export interface FeatureStatus {
  name: string;
  status: 'completed' | 'in-progress' | 'not-started';
  category: string;
}

export interface SyncOptions {
  dryRun?: boolean;
  verbose?: boolean;
  onProgress?: (message: string) => void;
}

export interface SyncResult {
  issuesCreated: number;
  issuesUpdated: number;
  issuesClosed: number;
  todosReplaced: number;
  errors: string[];
}

// ============================================================================
// GitHub API Client
// ============================================================================

class GitHubClient {
  constructor(
    private readonly token: string,
    private readonly repo: string, // owner/repo format
    private readonly options: SyncOptions = {}
  ) {}

  private log(message: string): void {
    if (this.options.verbose) {
      this.options.onProgress?.(message);
    }
  }

  /**
   * Parse owner and repo from "owner/repo" string
   */
  private parseRepo(): { owner: string; repo: string } {
    const match = this.repo.match(/^([^/]+)\/([^/]+)$/);
    if (!match) {
      throw new Error(`Invalid repo format: ${this.repo}. Expected "owner/repo"`);
    }
    return { owner: match[1], repo: match[2] };
  }

  /**
   * Sleep for a specified duration
   */
  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Make an authenticated request to GitHub API with retry logic
   */
  private async api<T>(
    endpoint: string,
    options: RequestInit = {},
    maxRetries: number = 3
  ): Promise<T> {
    const { owner, repo: repoName } = this.parseRepo();
    const url = `https://api.github.com/repos/${owner}/${repoName}${endpoint}`;

    if (this.options.dryRun) {
      this.log(`[DRY RUN] ${options.method || 'GET'} ${url}`);
      throw new Error('DRY_RUN_MODE');
    }

    let lastError: Error | null = null;
    const baseDelayMs = 1000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Accept': 'application/vnd.github.v3+json',
            'User-Agent': 'spacecode-sync',
            ...options.headers,
          },
        });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          const errorText = await response.text();
          throw new Error(
            `GitHub authentication failed (${response.status}). ` +
            `Please check your token:\n` +
            `  - Run 'gh auth token' to verify your token\n` +
            `  - Or set GITHUB_TOKEN environment variable\n` +
            `  - Ensure token has 'repo' and 'issues' scopes\n` +
            `Details: ${errorText}`
          );
        }

        // Handle rate limiting
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : baseDelayMs * Math.pow(2, attempt);

          if (attempt < maxRetries) {
            this.log(`Rate limited. Waiting ${waitMs}ms before retry...`);
            await this.sleep(waitMs);
            continue;
          }
        }

        // Handle server errors with retry
        if (response.status >= 500) {
          if (attempt < maxRetries) {
            const waitMs = baseDelayMs * Math.pow(2, attempt);
            this.log(`Server error ${response.status}. Retrying in ${waitMs}ms...`);
            await this.sleep(waitMs);
            continue;
          }
        }

        if (!response.ok) {
          const error = await response.text();
          throw new Error(`GitHub API error: ${response.status} - ${error}`);
        }

        return response.json() as Promise<T>;
      } catch (error) {
        lastError = error as Error;

        // Don't retry authentication errors
        if (lastError.message.includes('authentication failed')) {
          throw lastError;
        }

        // Don't retry dry run errors
        if (lastError.message === 'DRY_RUN_MODE') {
          throw lastError;
        }

        // Network errors - retry
        if (attempt < maxRetries && (lastError.message.includes('fetch') || lastError.message.includes('network'))) {
          const waitMs = baseDelayMs * Math.pow(2, attempt);
          this.log(`Network error. Retrying in ${waitMs}ms...`);
          await this.sleep(waitMs);
          continue;
        }
      }
    }

    throw lastError || new Error('Unknown error occurred');
  }

  /**
   * Find an issue by title
   */
  async findIssueByTitle(title: string): Promise<GitHubIssue | null> {
    try {
      const { owner, repo: repoName } = this.parseRepo();
      const query = encodeURIComponent(`${title} repo:${owner}/${repoName} state:open`);
      const url = `https://api.github.com/search/issues?q=${query}`;

      if (this.options.dryRun) {
        this.log(`[DRY RUN] GET ${url}`);
        return null;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Accept': 'application/vnd.github.v3+json',
          'User-Agent': 'spacecode-sync',
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json() as { items: Array<{ number: number; title: string; state: string; labels: Array<{ name: string }>; body: string; html_url: string }> };

      if (data.items.length === 0) {
        return null;
      }

      const item = data.items[0];
      return {
        number: item.number,
        title: item.title,
        state: item.state as 'open' | 'closed',
        labels: item.labels.map(l => l.name),
        body: item.body,
        url: item.html_url,
      };
    } catch (error) {
      console.warn(`Warning: GitHub issue search failed for "${title}": ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  /**
   * Create a new issue
   */
  async createIssue(
    title: string,
    body: string,
    labels: string[] = []
  ): Promise<number> {
    this.log(`Creating issue: ${title}`);

    const result = await this.api<{ number: number; html_url: string }>(
      '/issues',
      {
        method: 'POST',
        body: JSON.stringify({ title, body, labels }),
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return result.number;
  }

  /**
   * Update an issue (close/reopen)
   */
  async updateIssue(
    issueNumber: number,
    updates: { state?: 'open' | 'closed'; body?: string }
  ): Promise<void> {
    this.log(`Updating issue #${issueNumber}`);

    await this.api(`/issues/${issueNumber}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Get or create a GitHub Project
   * Note: GitHub Projects v2 uses a different API
   */
  async getOrCreateProject(projectName: string): Promise<GitHubProject | null> {
    this.log(`Looking for project: ${projectName}`);

    try {
      // Try to find existing project
      const { owner, repo: repoName } = this.parseRepo();

      // List projects for the repository
      const projects = await this.api<{ id: string; number: number; title: string; url: string }[]>(
        `/projects`,
        {
          headers: { 'Accept': 'application/vnd.github.inertia-preview+json' },
        }
      ).catch(() => []);

      const existing = projects.find(p => p.title === projectName);
      if (existing) {
        return {
          id: existing.id,
          owner,
          name: existing.title,
          number: existing.number,
          url: existing.url,
        };
      }

      // Create new project
      if (!this.options.dryRun) {
        this.log(`Creating project: ${projectName}`);
        const created = await this.api<{ id: string; number: number; title: string; url: string; html_url: string }>(
          `/projects`,
          {
            method: 'POST',
            body: JSON.stringify({
              name: projectName,
              body: 'Project tracking synced from PROGRESS.md',
            }),
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/vnd.github.inertia-preview+json',
            },
          }
        );

        return {
          id: created.id,
          owner,
          name: created.title,
          number: created.number,
          url: created.html_url || created.url,
        };
      }

      return null;
    } catch (error) {
      this.log(`Project error: ${error}`);
      return null;
    }
  }
}

// ============================================================================
// TODO Scanner
// ============================================================================

const TODO_PRIORITY_REGEX = /^(TODO|FIXME):\s*(HIGH|MEDIUM|LOW)?\s*/i;
const TODO_REGEX = /^\s*\/\/\s*(TODO|FIXME):\s*(.*)$/;
const CHECKBOX_REGEX = /^\s*[-*+]\s*\[[ x]\]\s*/;

/**
 * Scan codebase for TODO comments
 */
export async function scanTodos(
  root: string,
  options: SyncOptions = {}
): Promise<TodoComment[]> {
  const todos: TodoComment[] = [];
  const extensions = ['.ts', '.js', '.tsx', '.jsx', '.md'];

  async function scanDir(dir: string): Promise<void> {
    const entries = await readdir(dir, { withFileTypes: true });

    for (const entry of entries) {
      const fullPath = join(dir, entry.name);

      // Skip node_modules, dist, .git, etc.
      if (
        entry.name === 'node_modules' ||
        entry.name === 'dist' ||
        entry.name === '.git' ||
        entry.name === 'coverage' ||
        entry.name === 'build' ||
        entry.name === '.claude'
      ) {
        continue;
      }

      if (entry.isDirectory()) {
        await scanDir(fullPath);
      } else if (entry.isFile()) {
        const ext = fullPath.slice(fullPath.lastIndexOf('.'));
        if (extensions.includes(ext)) {
          await scanFile(fullPath);
        }
      }
    }
  }

  async function scanFile(filePath: string): Promise<void> {
    try {
      const content = await readFile(filePath, 'utf-8');
      const lines = content.split('\n');
      const relativePath = filePath.replace(root + '/', '');

      // Extract feature name from path (heuristic)
      const pathParts = relativePath.split('/');
      const feature = pathParts.length > 1
        ? pathParts[pathParts.length - 2]
        : 'general';

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const match = line.match(TODO_REGEX);

        if (match) {
          const type = match[1].toUpperCase() as 'TODO' | 'FIXME';
          const content = match[2].trim();

          // Parse priority from content
          let priority: 'HIGH' | 'MEDIUM' | 'LOW' = 'MEDIUM';
          const priorityMatch = content.match(/^(HIGH|MEDIUM|LOW)\s*:\s*/i);
          if (priorityMatch) {
            priority = priorityMatch[1].toUpperCase() as 'HIGH' | 'MEDIUM' | 'LOW';
          }

          todos.push({
            file: relativePath,
            line: i + 1,
            type,
            priority,
            content,
            feature,
          });

          options.onProgress?.(`Found ${type}: ${content.substring(0, 50)}...`);
        }
      }
    } catch (error) {
      options.onProgress?.(`Warning: Could not scan ${filePath}: ${error}`);
    }
  }

  await scanDir(root);
  return todos;
}

/**
 * Replace TODO comments with issue references in source files
 *
 * @param todos - The TODO comments that were synced
 * @param issueNumbers - Map of TODO content to issue numbers
 * @param root - Project root directory
 * @param options - Sync options
 */
export async function replaceTodosWithIssues(
  todos: TodoComment[],
  issueNumbers: Map<string, number>,
  root: string,
  options: SyncOptions = {}
): Promise<number> {
  if (options.dryRun) {
    options.onProgress?.('[DRY RUN] Would replace TODOs with issue references');
    return 0;
  }

  let replacedCount = 0;

  // Group todos by file for efficient processing
  const todosByFile = new Map<string, Array<TodoComment & { issueNumber: number }>>();
  for (const todo of todos) {
    const issueNumber = issueNumbers.get(`${todo.type}:${todo.content}`);
    if (issueNumber) {
      if (!todosByFile.has(todo.file)) {
        todosByFile.set(todo.file, []);
      }
      todosByFile.get(todo.file)!.push({ ...todo, issueNumber });
    }
  }

  // Process each file
  for (const [filePath, fileTodos] of todosByFile) {
    try {
      const fullPath = join(root, filePath);
      const content = await readFile(fullPath, 'utf-8');
      const lines = content.split('\n');

      // Process in reverse order to maintain line numbers
      const sortedTodos = [...fileTodos].sort((a, b) => b.line - a.line);

      for (const todo of sortedTodos) {
        const lineIndex = todo.line - 1;
        if (lineIndex >= 0 && lineIndex < lines.length) {
          const originalLine = lines[lineIndex];
          // Replace the TODO comment with issue reference
          // Format: // TODO: comment -> // Issue: #123
          const replacedLine = originalLine.replace(
            TODO_REGEX,
            `// Issue: #${todo.issueNumber} (${todo.type}: ${todo.content})`
          );

          if (replacedLine !== originalLine) {
            lines[lineIndex] = replacedLine;
            replacedCount++;
            options.onProgress?.(`Replaced ${todo.type} in ${filePath}:${todo.line} with issue #${todo.issueNumber}`);
          }
        }
      }

      // Write the updated content back
      await writeFile(fullPath, lines.join('\n'), 'utf-8');
    } catch (error) {
      options.onProgress?.(`Warning: Could not update ${filePath}: ${error}`);
    }
  }

  return replacedCount;
}

// ============================================================================
// Main Sync Function
// ============================================================================

/**
 * Main sync function - syncs to GitHub
 */
export async function syncToGitHub(
  repo: string,
  token: string,
  options: SyncOptions = {}
): Promise<SyncResult> {
  const result: SyncResult = {
    issuesCreated: 0,
    issuesUpdated: 0,
    issuesClosed: 0,
    todosReplaced: 0,
    errors: [],
  };

  const client = new GitHubClient(token, repo, options);

  try {
    // Get project root directory
    const root = process.cwd();

    // Step 1: Scan for TODOs
    options.onProgress?.('Scanning for TODO comments...');
    const todos = await scanTodos(root, options);

    // Track issue numbers for TODO replacement
    const issueNumbers = new Map<string, number>();

    // Step 2: Process each TODO
    for (const todo of todos) {
      try {
        // Create issue title from TODO
        const title = `[${todo.type}] ${todo.content}`;

        // Check if issue exists
        const existing = await client.findIssueByTitle(title);

        if (!existing) {
          // Create new issue
          const labels = [
            todo.type,
            `priority: ${todo.priority}`,
            `feature: ${todo.feature}`,
          ];

          const body = `**Type:** ${todo.type}\n` +
            `**Priority:** ${todo.priority}\n` +
            `**Feature:** ${todo.feature}\n` +
            `**Location:** \`${todo.file}:${todo.line}\`\n\n` +
            `---\n` +
            `*This issue was automatically created from a code comment.*`;

          const issueNumber = await client.createIssue(title, body, labels);
          result.issuesCreated++;

          // Track issue number for replacement
          issueNumbers.set(`${todo.type}:${todo.content}`, issueNumber);

          options.onProgress?.(`Created issue #${issueNumber} for ${todo.type}: ${todo.content}`);
        } else {
          // Track existing issue number
          issueNumbers.set(`${todo.type}:${todo.content}`, existing.number);
          options.onProgress?.(`Issue already exists for: ${todo.content}`);
        }
      } catch (error) {
        // Don't report DRY_RUN_MODE as an error - it's expected behavior
        const errorMsg = String(error);
        if (!errorMsg.includes('DRY_RUN_MODE')) {
          const msg = `Error processing TODO in ${todo.file}:${todo.line}: ${errorMsg}`;
          result.errors.push(msg);
          options.onProgress?.(msg);
        }
      }
    }

    // Step 3: Replace TODO comments with issue references
    if (issueNumbers.size > 0) {
      options.onProgress?.('\nReplacing TODO comments with issue references...');
      result.todosReplaced = await replaceTodosWithIssues(todos, issueNumbers, root, options);
    }

    options.onProgress?.(`\nSync complete: ${result.issuesCreated} issues created, ${result.todosReplaced} TODOs replaced`);
  } catch (error) {
    result.errors.push(String(error));
  }

  return result;
}

/**
 * Sync PROGRESS.md features to GitHub issues
 *
 * Creates issues for in-progress features and closes issues for completed features.
 */
export async function syncProgressToGitHub(
  repo: string,
  token: string,
  progressData: ProgressData,
  options: SyncOptions = {}
): Promise<SyncResult> {
  const result: SyncResult = {
    issuesCreated: 0,
    issuesUpdated: 0,
    issuesClosed: 0,
    todosReplaced: 0,
    errors: [],
  };

  const client = new GitHubClient(token, repo, options);

  try {
    // Step 1: Create issues for in-progress features
    for (const feature of progressData.features) {
      if (feature.status === 'in-progress') {
        try {
          const title = `[Feature] ${feature.name}`;
          const existing = await client.findIssueByTitle(title);

          if (!existing) {
            const labels = [
              'feature',
              `category: ${feature.category}`,
              'in-progress',
            ];

            const body = `**Feature:** ${feature.name}\n` +
              `**Category:** ${feature.category}\n` +
              `**Status:** In Progress\n\n` +
              `---\n` +
              `*This issue was automatically created from PROGRESS.md.*`;

            const issueNumber = await client.createIssue(title, body, labels);
            result.issuesCreated++;

            options.onProgress?.(`Created issue #${issueNumber} for in-progress feature: ${feature.name}`);
          }
        } catch (error) {
          // Don't report DRY_RUN_MODE as an error - it's expected behavior
          const errorMsg = String(error);
          if (!errorMsg.includes('DRY_RUN_MODE')) {
            const msg = `Error creating issue for feature ${feature.name}: ${errorMsg}`;
            result.errors.push(msg);
          }
        }
      }
    }

    // Step 2: Close issues for completed features
    for (const feature of progressData.features) {
      if (feature.status === 'completed') {
        try {
          const title = `[Feature] ${feature.name}`;
          const existing = await client.findIssueByTitle(title);

          if (existing && existing.state === 'open') {
            await client.updateIssue(existing.number, { state: 'closed' });
            result.issuesClosed++;

            options.onProgress?.(`Closed issue #${existing.number} for completed feature: ${feature.name}`);
          }
        } catch (error) {
          // Don't report DRY_RUN_MODE as an error - it's expected behavior
          const errorMsg = String(error);
          if (!errorMsg.includes('DRY_RUN_MODE')) {
            const msg = `Error closing issue for feature ${feature.name}: ${errorMsg}`;
            result.errors.push(msg);
          }
        }
      }
    }

    options.onProgress?.(`\nProgress sync complete: ${result.issuesCreated} issues created, ${result.issuesClosed} issues closed`);
  } catch (error) {
    result.errors.push(String(error));
  }

  return result;
}

// ============================================================================
// CLI Utilities
// ============================================================================

/**
 * Detect current git repo in "owner/repo" format
 */
export async function detectGitRepo(): Promise<string | null> {
  try {
    const url = await runOutput('git remote get-url origin');

    // Parse various git URL formats
    // https://github.com/owner/repo.git
    // git@github.com:owner/repo.git
    const httpsMatch = url.match(/github\.com[\/:]([^\/]+)\/([^\/\.]+)/);
    if (httpsMatch) {
      return `${httpsMatch[1]}/${httpsMatch[2]}`;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Read GitHub token from environment or gh CLI
 */
export async function getGitHubToken(): Promise<string | null> {
  // Check environment variable first
  const envToken = process.env.GITHUB_TOKEN || process.env.GH_TOKEN;
  if (envToken) {
    return envToken;
  }

  // Try to get token from gh CLI
  try {
    const token = await runOutput('gh auth token');
    return token || null;
  } catch {
    return null;
  }
}
